import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import {
  ExclamationTriangleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { FC, useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useCreateMutation } from "../../../api/genericHooks";
import { BookContext } from "../../../contexts/BookContext";

type Props = {
  open: boolean;
  setOpen: (isOpen: boolean) => void;
  resetSteps: () => void;
};

export const GenerateBookPDFModalStep5: FC<Props> = ({
  open,
  setOpen,
  resetSteps,
}) => {
  const { t } = useTranslation();
  const { book, refresh } = useContext(BookContext);

  // Mutations ===============================================================
  const generateBookPdf = useCreateMutation("/generate_book_pdf");

  // Submit =============================================================
  const onSubmit = useCallback(() => {
    generateBookPdf.mutateAsync({ id: book?.id }).then(() => {
      toast.success(t("generateBookPDFModal.sentToGenerationSuccess"));
      refresh();
      setOpen(false);
    });
  }, [refresh, t, setOpen]);

  const onCancel = useCallback(() => {
    setOpen(false);
    resetSteps();
  }, [setOpen, resetSteps]);

  return (
    <Dialog open={open} onClose={onCancel} className="relative z-10">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white text-slate-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-4xl sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
          >
            <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded bg-white text-slate-400 hover:text-slate-500 focus:outline-none focus:ring-2 focus:ring-delicate-500 focus:ring-offset-2"
              >
                <span className="sr-only">Close</span>
                <XMarkIcon aria-hidden="true" className="size-6" />
              </button>
            </div>
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                <DialogTitle
                  as="h3"
                  className="text-2xl font-semibold text-slate-800"
                >
                  {t("generateBookPDFModal.title")}
                </DialogTitle>
                <hr className="mt-2 w-full border-gray-200" />
                <div className="flex flex-row mt-5">
                  <div className="w-full">
                    <div className="sm:col-span-6">
                      <div className="rounded bg-delicate-50 p-4">
                        <div className="flex">
                          <div className="shrink-0">
                            <ExclamationTriangleIcon
                              aria-hidden="true"
                              className="size-5 text-delicate-900"
                            />
                          </div>
                          <div className="ml-3">
                            <h3 className="text-sm font-medium text-delicate-900">
                              {t("generateBookPDFModal.attention")}
                            </h3>
                            <div className="mt-2 text-sm text-slate-800">
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: t("generateBookPDFModal.attention2", {
                                    interpolation: {
                                      escapeValue: false,
                                    },
                                  }),
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                onClick={() => onSubmit()}
                className="ml-3 mt-3 inline-flex w-full justify-center rounded bg-rust-50 px-3 py-2 text-sm font-semibold text-white shadow-sm ring-1 ring-inset ring-rust-300 hover:bg-rust-100 sm:mt-0 sm:w-auto"
              >
                {t("addPicturesModal.save")}
              </button>
              <button
                type="button"
                onClick={() => onCancel()}
                className="mt-3 inline-flex w-full justify-center rounded bg-slate-50 px-3 py-2 text-sm font-semibold text-white shadow-sm ring-1 ring-inset ring-slate-300 hover:bg-slate-100 sm:mt-0 sm:w-auto"
              >
                {t("addPicturesModal.cancel")}
              </button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};
